import { BANNER_KEYS, BANNER_TYPES, BANNER_VARIANTS, BUTTON_STYLE, REGEX_PATTERNS, WORKSPACE_ENVIRONMENT } from '@/app.constants';
import Banner from '@/components/ui/banner/Banner';
import Button from '@/components/ui/button/index.tsx';
import InputField from '@/components/ui/inputfield';
import SideSheet from '@/components/ui/side-sheet/side-sheet';
import SidesheetFooter from '@/components/ui/sidesheet-footer';
import toast from '@/config/react-toast';
import { CustomHeader, getCustomBaseUrl, getCustomHeaders, removeCustomBaseUrl, removeCustomHeaders, setCustomBaseUrl, setCustomHeaders } from '@/utils/developer-settings';
import { Plus, Server, Trash2, Wifi, Wrench } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import PerfectScrollbar from 'react-perfect-scrollbar';

interface FormData {
  customBaseUrl: string;
  customHeaders: CustomHeader[];
}

const DeveloperSettingsModalContent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const customHeaders = getCustomHeaders();
  const customBaseUrl = getCustomBaseUrl();
  const areCustomSettingsPresent = customHeaders.length > 0 || !!customBaseUrl;

  const form = useForm<FormData>({
    defaultValues: {
      customBaseUrl: '',
      customHeaders: [{ key: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'customHeaders',
  });

  const handleOpenModal = () => {
    setIsOpen(true);
    form.reset({
      customBaseUrl: customBaseUrl,
      customHeaders: customHeaders.length > 0 ? customHeaders : [{ key: '', value: '' }],
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (data: FormData) => {
    const validHeaders = data.customHeaders.filter((header) => header.key.trim() && header.value.trim());
    const newBaseUrl = data.customBaseUrl.trim().replace(/\/$/, '');
    const hasBaseUrlChanged = newBaseUrl !== (customBaseUrl || '');

    if (hasBaseUrlChanged && (customBaseUrl || newBaseUrl)) {
      // If Base URL changed, clear everything but then restore custom settings
      localStorage.clear();
      setCustomHeaders(validHeaders);
      setCustomBaseUrl(newBaseUrl);
      toast('Base URL changed. Session cleared.', { type: 'success' });
    } else {
      setCustomHeaders(validHeaders);
      setCustomBaseUrl(newBaseUrl);
      
      const wasAlreadyEmpty = !customHeaders.length && !customBaseUrl;
      const isNowEmpty = !validHeaders.length && !newBaseUrl;

      if (isNowEmpty) {
        if (!wasAlreadyEmpty) {
          toast('All custom settings cleared', { type: 'success' });
        }
      } else {
        toast('Developer Settings saved successfully', { type: 'success' });
      }
    }
    
    setIsOpen(false);
    window.location.reload();
  };

  const handleAddRow = () => {
    const currentHeaders = form.getValues('customHeaders');
    const lastHeader = currentHeaders[currentHeaders.length - 1];

    if (lastHeader && (!lastHeader.key?.trim() || !lastHeader.value?.trim())) {
      // @ts-ignore
      toast('Please fill the current row before adding a new one', { type: 'warning' });
      return;
    }
    append({ key: '', value: '' });
  };

  const handleRemoveRow = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    } else {
      form.reset({ ...form.getValues(), customHeaders: [{ key: '', value: '' }] });
    }
  };

  const handleRemoveAll = () => {
    removeCustomHeaders();
    removeCustomBaseUrl();
    form.reset({ 
      customBaseUrl: '',
      customHeaders: [{ key: '', value: '' }] 
    });
    toast('All custom settings removed', { type: 'success' });
    setIsOpen(false);
    window.location.reload();
  };

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <>
      <span 
        id='omniful-dev-settings' 
        onClick={handleOpenModal} 
        className='cursor-pointer bg-primary_lighter hover:bg-[#EDE9FE] rounded-full p-2 relative transition-colors flex items-center justify-center'
      >
        <Wrench size={24} className='text-primary' />
        {areCustomSettingsPresent && (
          <div className='absolute bottom-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-1 ring-white' />
        )}
      </span>

      <SideSheet
        isOpen={isOpen}
        handleModalFunc={handleClose}
        title='Developer Settings'
        size='md'
      >
        <PerfectScrollbar className="h-full">
          <form 
            className='flex flex-col h-full' 
            onSubmit={form.handleSubmit(onSubmit)} 
            onKeyDown={handleFormKeyDown}
          >
            <div className='flex flex-col gap-6 p-6 pb-24'>
              <Banner
                type={BANNER_TYPES.NOTIFICATION}
                variant={BANNER_VARIANTS.WARNING}
                bannerKey={BANNER_KEYS.DEVELOPER_CORS_BANNER}
              >
                <div className='flex flex-col gap-1'>
                  <div className='text-xs'>
                    Facing CORS issues? Run this command in your terminal to open Chrome with web security disabled:
                  </div>
                  <code className='bg-orange-100 text-orange-800 px-2 py-1 rounded text-[10px] font-mono break-all'>
                    {window.navigator.platform.toLowerCase().includes('mac') 
                      ? 'open -n -a /Applications/Google\\ Chrome.app --args --user-data-dir="/tmp/chrome_dev_test" --disable-web-security'
                      : window.navigator.platform.toLowerCase().includes('win')
                        ? 'chrome.exe --user-data-dir="C:/chrome_dev_test" --disable-web-security --disable-site-isolation-trials'
                        : 'google-chrome --user-data-dir="/tmp/chrome_dev_test" --disable-web-security'
                    }
                  </code>
                </div>
              </Banner>
              {/* Connection Settings */}
              <div className='bg-white border border-primary_lighter rounded-xl p-4 shadow-sm flex flex-col gap-3'>
                <div className='flex items-center gap-2'>
                  <Wifi size={20} className='text-primary' />
                  <div className='text-base font-semibold text-dark'>Connection Settings</div>
                </div>
                <p className='text-xs text-dark_6'>
                  Override the API endpoint for this session. This is an advanced dev setting.
                </p>
                <InputField
                  label='Custom Base URL'
                  placeholder='e.g., http://localhost:8000'
                  {...form.register('customBaseUrl', {
                    pattern: {
                      value: REGEX_PATTERNS.URL_VALIDATION,
                      message: 'Please enter a valid URL including protocol (e.g., http://localhost:8000)'
                    }
                  })}
                  errors={form.formState.errors.customBaseUrl?.message || ''}
                />
              </div>

              {/* Request Headers */}
              <div className='bg-white border border-primary_lighter rounded-xl p-4 shadow-sm flex flex-col gap-3'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Server size={20} className='text-primary' />
                    <div className='text-base font-semibold text-dark'>Request Headers</div>
                  </div>
                  <Button 
                    type='button' 
                    ofStyle={BUTTON_STYLE.NO_BACKGROUND}
                    onClick={handleAddRow}
                    className="p-1 h-auto"
                  >
                    <Plus size={14} className='mr-1' />
                    <span className="text-xs">Add Row</span>
                  </Button>
                </div>
                <p className='text-xs text-dark_6'>
                  Inject custom commit id, feature flags, or debug headers.
                </p>

                <div className='space-y-3 mt-2'>
                  {fields.map((field, index) => (
                    <div key={field.id} className='flex items-start gap-2'>
                      <div className="flex-1">
                        <InputField
                          label=""
                          placeholder='Key'
                          {...form.register(`customHeaders.${index}.key` as const)}
                        />
                      </div>
                      <div className="flex-1">
                        <InputField
                          label=""
                          placeholder='Value'
                          {...form.register(`customHeaders.${index}.value` as const)}
                        />
                      </div>
                      <button
                        type='button'
                        onClick={() => handleRemoveRow(index)}
                        disabled={fields.length === 1}
                        className='mt-2.5 text-danger hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <Banner
                type='NOTIFICATION'
                variant='INFO'
                text='Example: X-Gateway-CommitID : 213027c (Uses the first 7 characters of the commit hash)'
                bannerKey='custom-header-tip'
              />  
            </div>

            <SidesheetFooter className='flex justify-end gap-3 sticky bottom-0 mt-auto bg-white border-t p-4'>
              <Button type='button' ofStyle={BUTTON_STYLE.OUTLINED} onClick={handleRemoveAll}>
                Clear All
              </Button>
              <Button type='submit'>
                Apply Settings
              </Button>
            </SidesheetFooter>
          </form>
        </PerfectScrollbar>
      </SideSheet>
    </>
  );
};

const DeveloperSettingsWrapper = () => {
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === WORKSPACE_ENVIRONMENT.DEVELOPMENT;
  
  if (!isDevelopment) {
    return null;
  }
  
  return <DeveloperSettingsModalContent />;
};

export default DeveloperSettingsWrapper;
